import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, Scale, Calculator, FileCheck, Activity, BadgeCheck, ArrowUpRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ChatWidget from "@/components/ChatWidget";

// Modern minimalist architectural image — grayscale, used as the hero's right-side feature
// (bleeds in with a soft fade into the white text area, exactly like the EH Inc brand card).
const HERO_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80";
const PROPERTY_IMG = "https://images.unsplash.com/photo-1762529388467-728757c7a446?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjYXBlJTIwdG93biUyMHByb3BlcnR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3ODU4MjYwNXww&ixlib=rb-4.1.0&q=85";
const ABSTRACT_IMG = "https://static.prod-images.emergentagent.com/jobs/24f62983-37e7-4028-8899-d2da2e288f39/images/de2bed1f2e2058016b67b4238b7dba57051cfd9d16f768ebb4aeac66fff355ed.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="landing-page">
      <SiteHeader />

      {/* HERO — typography-first, conversion-focused, brand-card architectural image */}
      <section className="relative border-b border-zinc-200 bg-white overflow-hidden">
        {/* Right-side architectural image — present, not dominant. Bleeds in with a soft fade. */}
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-full lg:w-[58%] pointer-events-none"
          data-testid="hero-backdrop"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              filter: "grayscale(1) contrast(1.05)",
            }}
          />
          {/* Left-edge fade: bright white at the type, transparent into the image */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, #ffffff 0%, #ffffff 22%, rgba(255,255,255,0.92) 38%, rgba(255,255,255,0.55) 58%, rgba(255,255,255,0.15) 82%, rgba(255,255,255,0) 100%)",
            }}
          />
          {/* Subtle slate wash to match the brand palette */}
          <div className="absolute inset-0 mix-blend-multiply" style={{ background: "linear-gradient(to right, transparent 60%, rgba(90,107,134,0.08) 100%)" }} />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pt-10 pb-14 lg:pt-14 lg:pb-16">
          <div className="text-overline text-[#5a6b86] tracking-[0.22em]">
            Conveyancing · Antenuptial Contracts · Wills & Trusts · Estates
          </div>

          <h1
            className="mt-6 font-serif text-[44px] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-[88px] xl:leading-[0.98] font-medium tracking-tight max-w-4xl"
            data-testid="hero-headline"
          >
            Know the cost.
            <br />
            Know what&apos;s
            {" "}<em className="not-italic text-[#5a6b86] font-serif italic">required</em>.
          </h1>

          <p className="mt-6 text-zinc-700 text-base lg:text-lg max-w-xl leading-relaxed">
            Start your legal process with clarity. See your costs upfront, understand what&apos;s required,
            and get guided through the right service — whether you&apos;re buying property, getting married,
            planning your estate, or setting up a trust.
          </p>

          {/* CTA — calculator-first */}
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link
              to="/calculators"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-7 py-4 text-sm font-medium tracking-wide hover:bg-zinc-800 transition-colors"
              data-testid="hero-cta-calculator"
            >
              Calculate your costs <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-900 hover:text-[#5a6b86] transition-colors group"
              data-testid="hero-cta-intake"
            >
              Get started
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Quick links — clickable cards that route to the underlying flow */}
          <div className="mt-12 lg:mt-14 max-w-3xl grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4 border-t border-zinc-200 pt-8" data-testid="hero-icon-row">
            <QuickLink
              to="/calculators"
              icon={Calculator}
              label="Exact cost"
              sub="SARS · Deeds · VAT"
              testid="hero-icon-exact-cost"
            />
            <QuickLink
              to="/calculators#insight"
              icon={FileCheck}
              label="What's required"
              sub="FICA · OTP · clearances"
              testid="hero-icon-what-s-required"
            />
            <QuickLink
              to="/find-my-matter"
              icon={Activity}
              label="Live progress"
              sub="Offer → registration"
              testid="hero-icon-live-progress"
            />
            <QuickLink
              to="/intake"
              icon={BadgeCheck}
              label="Lawyer-reviewed"
              sub="Every document"
              testid="hero-icon-lawyer-reviewed"
            />
          </div>
        </div>
      </section>

      {/* SERVICES — bento */}
      <section id="services" className="border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="flex items-end justify-between gap-8 flex-wrap mb-10">
            <div className="max-w-xl">
              <div className="text-overline text-zinc-500">Practice areas</div>
              <h2 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight">
                Six disciplines, one firm. Three live today.
              </h2>
            </div>
            <p className="text-zinc-600 max-w-md text-sm leading-relaxed">
              We focus narrowly so we can move quickly. Every matter is opened through a guided intake, then reviewed and progressed by an admitted attorney.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <ServiceCard
              span="lg:col-span-7"
              icon={<Scale className="h-5 w-5" />}
              tag="01 · Conveyancing"
              title="Property transfers, bond cancellations & new bonds."
              desc="The full deeds-office lifecycle — from FICA and OTP to lodgement and registration. SARS transfer duty handled in-house."
              img={PROPERTY_IMG}
            />
            <ServiceCard
              span="lg:col-span-5"
              icon={<FileText className="h-5 w-5" />}
              tag="02 · Antenuptial Contracts"
              title="ANCs — with or without accrual."
              desc="Drafted, notarised and registered before the wedding day. Plain-language explanations included."
              img={ABSTRACT_IMG}
            />
            <ServiceCard
              span="lg:col-span-5"
              dark
              icon={<ShieldCheck className="h-5 w-5" />}
              tag="03 · Wills & Trusts"
              title="Wills, inter-vivos & testamentary trusts."
              desc="Will drafting, trustee selection, master registration and ongoing administration support — for family wealth and asset protection."
            />
            <ServiceCard
              span="lg:col-span-7"
              icon={<FileText className="h-5 w-5" />}
              tag="04 · Deceased Estate Administration"
              title="End-to-end estate administration."
              desc="From reporting the estate to the Master through to L&D account, distribution and final discharge — handled with care, on a published timeline."
            />
          </div>
        </div>
      </section>

      {/* CALCULATORS — Start with clarity */}
      <section id="calculators" className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-overline text-zinc-500" data-testid="start-with-clarity-overline">Calculators</div>
            <h2 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight" data-testid="start-with-clarity-heading">
              Start with clarity.
            </h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              Three calculators built for real South African legal practice. Understand the numbers — then continue
              into our platform if you'd like a lawyer to take it from here.
            </p>
          </div>

          {/* Three calculator cards */}
          <div className="grid lg:grid-cols-3 gap-5" data-testid="start-with-clarity-cards">
            <CalcCard
              kicker="01 · Family · Divorce"
              title="Maintenance Calculator"
              blurb="Understand possible maintenance before legal action."
              href="/calculators/maintenance"
              cta="Calculate Maintenance"
              testid="calc-card-maintenance"
            />
            <CalcCard
              kicker="02 · Estates"
              title="Deceased Estate Calculator"
              blurb="Estimate estate costs, duty, and liquidity risks."
              href="/calculators/deceased-estate"
              cta="Estimate Estate"
              testid="calc-card-estate"
            />
            <CalcCard
              kicker="03 · Property"
              title="Transfer Cost Calculator"
              blurb="SARS duty, conveyancing fees and the full transfer cost in one view."
              href="/calculators"
              cta="Calculate Transfer Costs"
              testid="calc-card-transfer"
              dark
            />
          </div>

          {/* "What do you need help with?" — interactive routing */}
          <div className="mt-20 border-t border-zinc-200 pt-16">
            <div className="text-overline text-zinc-500" data-testid="help-router-overline">Not sure where to begin?</div>
            <h3 className="mt-3 font-serif text-3xl lg:text-4xl font-medium tracking-tight" data-testid="help-router-heading">
              What do you need help with?
            </h3>
            <p className="mt-3 text-zinc-600 text-sm max-w-2xl">
              Pick the situation that matches yours — we'll take you straight to the right calculator.
            </p>

            <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="help-router-options">
              <HelpRoute
                icon={<Scale className="h-5 w-5" />}
                title="Divorce / Maintenance"
                blurb="Working out maintenance, support, or a divorce settlement."
                href="/calculators/maintenance"
                testid="help-route-maintenance"
              />
              <HelpRoute
                icon={<FileText className="h-5 w-5" />}
                title="Deceased Estate"
                blurb="A family member has passed away and you need to wind up their estate."
                href="/calculators/deceased-estate"
                testid="help-route-estate"
              />
              <HelpRoute
                icon={<Calculator className="h-5 w-5" />}
                title="Property Transfer"
                blurb="Buying or selling a property and need to know the transfer cost."
                href="/calculators"
                testid="help-route-transfer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI VALUE PROP */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-overline text-zinc-500">How it works</div>
            <h2 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight">
              AI handles the paperwork. <br />
              <span className="text-zinc-500">Our lawyers handle the law.</span>
            </h2>
            <ol className="mt-10 space-y-6">
              {[
                ["Online intake", "Tell us about your matter through a few guided questions. 10–15 minutes — no firm-visit needed."],
                ["Attorney review", "Your matter is reviewed by an admitted attorney within one business day."],
                ["Live status", "Track every stage via WhatsApp and email — from opening through to completion."],
                ["Completion", "We finalise the work and keep you informed. You receive the outcome (or the proceeds)."],
              ].map(([t, d], i) => (
                <li key={i} className="grid grid-cols-12 gap-4">
                  <div className="col-span-1 font-mono text-sm text-zinc-400">0{i + 1}</div>
                  <div className="col-span-11">
                    <div className="font-semibold">{t}</div>
                    <div className="text-zinc-600 text-sm leading-relaxed mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <Link
              to="/intake"
              className="mt-10 inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-4 font-medium hover:bg-zinc-800 transition-colors"
              data-testid="how-cta-intake"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 p-10">
            <div className="text-overline text-zinc-500 mb-6">A note on AI</div>
            <p className="text-zinc-700 leading-relaxed">
              Every document drafted by AI is reviewed by an admitted conveyancer before it reaches you. AI is a
              tool — not a substitute for legal judgement. Drafts will always carry a clear
              <span className="font-mono mx-1 px-1.5 py-0.5 bg-zinc-900 text-white text-xs">DRAFT</span>
              marker until signed off.
            </p>
            <p className="mt-4 text-sm text-zinc-500">Information shared with us is treated as legally privileged and securely stored.</p>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-b border-zinc-200 bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-overline text-zinc-400">About EH Inc</div>
            <h2 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight">A small firm built for the next decade.</h2>
          </div>
          <div className="lg:col-span-7 text-zinc-300 leading-relaxed">
            <p>
              EH Inc was founded on the belief that South African legal services should be accessible, transparent and
              fast — without sacrificing the depth required by complex property and family law work. We are admitted
              conveyancers and notaries operating from Cape Town, serving clients nationally.
            </p>
            <div className="mt-10 grid sm:grid-cols-3 gap-8">
              <Stat label="Matters since 2015" value="1,200+" />
              <Stat label="Average lodgement" value="14 days" />
              <Stat label="Client NPS" value="71" />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-overline text-zinc-500">Contact</div>
            <h2 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight">Ready to begin?</h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              The fastest way to start is to answer a few guided questions online. Prefer to chat first? Use the assistant in the corner — or call us directly.
            </p>

            <div className="mt-10 border-l-2 border-zinc-200 pl-5">
              <div className="font-serif text-2xl text-zinc-900">Estelle Holtzhausen</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Attorney &amp; Conveyancer</div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm">
              <div>
                <div className="text-overline text-zinc-500 mb-1">Phone</div>
                <a href="tel:+27823011291" className="text-zinc-900 hover:underline">082 301 1291</a>
              </div>
              <div>
                <div className="text-overline text-zinc-500 mb-1">Website</div>
                <a href="https://www.ehinc.co.za" className="text-zinc-900 hover:underline">www.ehinc.co.za</a>
              </div>
              <div>
                <div className="text-overline text-zinc-500 mb-1">Email — Estelle</div>
                <a href="mailto:estelle@ehinc.co.za" className="text-zinc-900 hover:underline">estelle@ehinc.co.za</a>
              </div>
              <div>
                <div className="text-overline text-zinc-500 mb-1">Email — Admin</div>
                <a href="mailto:admin@ehinc.co.za" className="text-zinc-900 hover:underline">admin@ehinc.co.za</a>
              </div>
              <div className="sm:col-span-2">
                <div className="text-overline text-zinc-500 mb-1">Office</div>
                <div className="text-zinc-900">Dunbrook Place, 1st Floor Building B</div>
                <div className="text-zinc-700">151 Nicolson Road, Brooklyn, Pretoria</div>
              </div>
            </div>
          </div>
          <div className="border border-zinc-200 p-10 bg-zinc-50">
            <h3 className="font-serif text-3xl">Start your matter</h3>
            <p className="mt-2 text-zinc-600 text-sm">
              A few guided questions — 10 to 15 minutes. We open the file the same day.
            </p>
            <Link
              to="/intake"
              className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-4 font-medium hover:bg-zinc-800 transition-colors w-full justify-center"
              data-testid="contact-cta-intake"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
      <ChatWidget />
    </div>
  );
}

function ServiceCard({ span, icon, tag, title, desc, img, dark }) {
  return (
    <div
      className={`${span} relative overflow-hidden border border-zinc-200 ${dark ? "bg-zinc-900 text-white" : "bg-white"} p-8 lg:p-12 min-h-[320px] flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300`}
      data-testid="service-card"
    >
      {img && (
        <img
          src={img}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
      )}
      <div className="relative">
        <div className={`flex items-center gap-2 text-overline ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {icon} {tag}
        </div>
        <h3 className={`mt-6 font-serif text-3xl lg:text-4xl font-medium tracking-tight max-w-md ${dark ? "" : ""}`}>{title}</h3>
      </div>
      <p className={`relative mt-6 text-sm max-w-md leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-600"}`}>{desc}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-4xl font-medium">{value}</div>
      <div className="text-overline text-zinc-500 mt-2">{label}</div>
    </div>
  );
}

function HeroIcon({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-start gap-3" data-testid={`hero-icon-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="h-10 w-10 grid place-items-center border border-[#5a6b86]/30 text-[#5a6b86] shrink-0">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <div className="font-serif text-base lg:text-lg leading-tight text-zinc-900">{label}</div>
        <div className="text-[11px] tracking-wider uppercase text-zinc-500 mt-1">{sub}</div>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative bg-white/85 backdrop-blur-sm border border-zinc-200 hover:border-zinc-900 hover:bg-white transition-all duration-300 px-4 py-5 lg:px-5 lg:py-6 flex flex-col gap-4 min-h-[148px]"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 grid place-items-center bg-zinc-50 group-hover:bg-zinc-900 group-hover:text-white text-[#5a6b86] transition-colors duration-300">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
        </div>
        <ArrowUpRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-serif text-lg lg:text-xl leading-tight text-zinc-900">{label}</div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mt-2 leading-relaxed">{sub}</div>
      </div>
    </Link>
  );
}


/* ── Start-with-clarity calculator card ─────────────────────────────── */

function CalcCard({ kicker, title, blurb, href, cta, testid, dark }) {
  return (
    <Link
      to={href}
      data-testid={testid}
      className={`group block border transition-all duration-300 p-8 lg:p-9 ${
        dark
          ? "bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800"
          : "bg-white border-zinc-200 hover:border-zinc-900"
      }`}
    >
      <div className={`text-[10px] tracking-[0.18em] uppercase ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{kicker}</div>
      <h3 className="mt-4 font-serif text-2xl lg:text-3xl leading-tight tracking-tight">{title}</h3>
      <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-600"}`}>{blurb}</p>
      <div className={`mt-8 inline-flex items-center gap-2 text-sm font-medium ${dark ? "text-white" : "text-zinc-900"}`}>
        <span>{cta}</span>
        <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}


/* ── "What do you need help with?" interactive routing tile ─────────── */

function HelpRoute({ icon, title, blurb, href, testid }) {
  return (
    <Link
      to={href}
      data-testid={testid}
      className="group border border-zinc-200 bg-white hover:border-zinc-900 hover:bg-zinc-50 transition-all p-6 block"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 grid place-items-center bg-zinc-100 group-hover:bg-zinc-900 group-hover:text-white text-zinc-700 transition-colors duration-300">
          {icon}
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className="mt-5 font-serif text-xl leading-tight text-zinc-900">{title}</div>
      <p className="mt-2 text-xs text-zinc-600 leading-relaxed">{blurb}</p>
    </Link>
  );
}
